import { useState, useRef, useEffect } from 'react';
import Modal from './Modal.jsx';
import Button from './Button.jsx';
import FormField from './FormField.jsx';
import { useUpdateCategory, useDeleteCategory } from '../hooks/useCategories.js';

export default function CategoryEditModal({ category, open, onClose }) {
  const [name, setName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const nameRef = useRef(null);
  const update = useUpdateCategory();
  const remove = useDeleteCategory();

  useEffect(() => {
    if (open && category) {
      setName(category.name);
      setConfirmDelete(false);
      update.reset?.();
      remove.reset?.();
      const t = setTimeout(() => nameRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open, category]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleRename(e) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || trimmed === category.name) return;
    try {
      await update.mutateAsync({ id: category.id, name: trimmed });
      onClose();
    } catch {
      // Error displayed via update.error
    }
  }

  async function handleDelete() {
    try {
      await remove.mutateAsync(category.id);
      onClose();
    } catch {
      // Error displayed via remove.error
    }
  }

  if (!category) return null;

  const parentName = category.parent_name ?? 'la catégorie parente';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Modifier « ${category.name} »`}
    >
      <div className="space-y-5">
        {/* Rename section */}
        {!confirmDelete && (
          <form onSubmit={handleRename} className="space-y-3">
            <FormField label="Nom" htmlFor="cem-name" required>
              <input
                ref={nameRef}
                id="cem-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                required
                className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
              />
            </FormField>

            {update.error && (
              <div
                className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                role="alert"
              >
                {update.error.message}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={onClose} disabled={update.isPending}>
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={!name.trim() || name.trim() === category.name}
                loading={update.isPending}
              >
                Renommer
              </Button>
            </div>
          </form>
        )}

        {/* Divider + Delete trigger */}
        {!confirmDelete && (
          <div className="border-t border-slate-100 pt-4">
            <Button
              type="button"
              variant="danger"
              className="w-full"
              onClick={() => setConfirmDelete(true)}
            >
              Supprimer cette sous-catégorie…
            </Button>
          </div>
        )}

        {/* Delete confirmation inline */}
        {confirmDelete && (
          <div className="space-y-4">
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800 leading-relaxed">
              <p className="font-semibold mb-1">Supprimer «&nbsp;{category.name}&nbsp;» ?</p>
              <p>
                Les transactions attribuées reviendront sur «&nbsp;{parentName}&nbsp;».
                Les règles d'attribution liées et les lignes de budget seront supprimées.
              </p>
            </div>

            {remove.error && (
              <div
                className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                role="alert"
              >
                {remove.error.message}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setConfirmDelete(false)}
                disabled={remove.isPending}
              >
                Annuler
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={handleDelete}
                loading={remove.isPending}
              >
                Confirmer la suppression
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
